export interface ReadingLocator {
  episodeId: string;
  paragraphIndex: number;
  charOffset: number;
}

/**
 * elementFromPoint로 locator를 추출합니다. O(1) 조회로 성능 최적화.
 */
const extractLocatorFromElement = (charEl: HTMLElement): ReadingLocator | null => {
  const epId = charEl.dataset.sceneEpisodeId;
  const pIdx = charEl.dataset.paragraphIndex;
  const charOffset = charEl.dataset.charOffset;

  if (epId == null || pIdx == null || charOffset == null) return null;

  return {
    episodeId: epId,
    paragraphIndex: Number(pIdx),
    charOffset: Number(charOffset)
  };
};

/**
 * 컨테이너 내 보이는 영역에서 가장 왼쪽 상단의 문자 위치를 반환합니다.
 * @param container - 스크롤 컨테이너 요소
 * @returns 보이는 첫 문자의 locator 또는 null
 */
export const getVisibleReadingLocator = (container: HTMLElement): ReadingLocator | null => {
  const containerRect = container.getBoundingClientRect();
  const { left, top, right, bottom, width, height } = containerRect;

  // 보이는 영역 상단·좌측에서 샘플링 (읽기 순서상 첫 문자 근처)
  const samplePoints: [number, number][] = [
    [left + 20, top + 20],
    [left + width / 2, top + 20],
    [left + 20, top + height / 4],
    [left + 50, top + 50]
  ];

  for (const [x, y] of samplePoints) {
    if (x < left || x > right || y < top || y > bottom) continue;

    const el = document.elementFromPoint(x, y);
    if (!el || !container.contains(el)) continue;

    const charEl = el.closest<HTMLElement>('[data-reading-char="true"]');
    if (!charEl) continue;

    const loc = extractLocatorFromElement(charEl);
    if (loc) return loc;
  }

  return null;
};

export interface ReadingProgress {
  currentIndex: number;
  totalChars: number;
  episodeId?: string;
}

/**
 * 컨테이너 내 보이는 영역에서 읽기 순서상 가장 뒤에 있는 글자의 인덱스와 전체 글자 수를 반환합니다.
 * @param container - 스크롤 컨테이너 요소
 * @returns { currentIndex, totalChars } 또는 null
 */
export const getReadingProgress = (container: HTMLElement): ReadingProgress | null => {
  const allChars = container.querySelectorAll<HTMLElement>('[data-reading-char="true"]');
  if (allChars.length === 0) return null;

  const containerRect = container.getBoundingClientRect();
  let lastVisibleIndex = -1;
  let lastVisibleEpisodeId: string | undefined;

  for (let i = 0; i < allChars.length; i++) {
    const rect = allChars[i].getBoundingClientRect();
    if (
      rect.right > containerRect.left &&
      rect.left < containerRect.right &&
      rect.bottom > containerRect.top &&
      rect.top < containerRect.bottom
    ) {
      lastVisibleIndex = i;
      const epId = allChars[i].dataset.sceneEpisodeId;
      if (epId != null) lastVisibleEpisodeId = epId;
    }
  }

  if (lastVisibleIndex < 0) return null;

  return {
    currentIndex: lastVisibleIndex,
    totalChars: allChars.length,
    ...(lastVisibleEpisodeId != null && { episodeId: lastVisibleEpisodeId })
  };
};
